import { CategoryPillRow } from '../CategoryPillRow';
import { MyPacksButton } from './MyPacksButton';
import { useIsScrolled } from './useIsScrolled';

export interface HeaderCategory {
  id: string;
  label: string;
  iconSrc?: string;
}

export interface HeaderProps {
  categories: HeaderCategory[];
  activeCategoryId?: string;
  onCategorySelect?: (id: string) => void;
  onMyPacksClick?: () => void;
  /** When set (anonymous embed visitor — no session token), the right slot
   *  renders a "Login" button instead of "My Packs". The template owns the
   *  auth wiring; this primitive stays auth-agnostic. */
  onLogin?: () => void;
  className?: string;
}

/**
 * Floating glass header for the pack-detail / shop page.
 *
 * Outer `<header>` is full-width sticky so the glass / scroll-state
 * background bleeds edge-to-edge. The inner wrapper mirrors the page
 * content container (`mx-auto max-w-350` + `px-4 / @[600px]:px-6 /
 * @[900px]:px-8`) so pills and My Packs line up vertically with the
 * PackLayout grid below.
 *
 * Inner grid is `[minmax(0,1fr)] [auto]`: pills hug the left, My Packs
 * stays flush to the container's right edge. The pill wrapper is hidden
 * under 600px and the 1fr track collapses to 0, so the mobile layout
 * becomes `[empty] [My Packs]` with My Packs at the right edge.
 */
export function Header({
  categories,
  activeCategoryId,
  onCategorySelect,
  onMyPacksClick,
  onLogin,
  className = '',
}: HeaderProps) {
  const scrolled = useIsScrolled(8);

  return (
    <header
      data-scrolled={scrolled || undefined}
      data-co-component="Header"
      className={
        // `top-[var(--page-safe-top)]`, NOT the `header-safe-top` bleed the
        // other page headers use: the classic Pack page deliberately pins its
        // mobile Just Pulled strip ABOVE this bar (Figma 226:2001), and a
        // header lifted into the inset would cover that strip — including its
        // taps. This bar parks below the inset instead.
        '@container sticky top-[var(--page-safe-top)] z-40 ' +
        'py-5 ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
        (scrolled
          ? 'bg-black/40 backdrop-blur-md border-b border-white/10 '
          : 'border-b border-transparent ') +
        className
      }
    >
      {/* DS override layer: --co-Header-bg / --co-Header-text. Rendered as an
          absolute inset span so it doesn't interfere with scroll-state Tailwind
          background classes (inline style would win over class). When the CSS
          vars are unset the span is transparent and invisible. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-[1]"
        style={{ background: 'var(--co-Header-bg)', color: 'var(--co-Header-text)' }}
      />

      <div
        className="
          mx-auto max-w-350
          px-4 @[600px]:px-6 @[900px]:px-8
          grid grid-cols-[minmax(0,1fr)_auto] items-center gap-6
        "
      >
        <div className="hidden min-w-0 @[600px]:block">
          <CategoryPillRow
            categories={categories}
            centered={false}
            {...(activeCategoryId !== undefined ? { activeId: activeCategoryId } : {})}
            {...(onCategorySelect ? { onSelect: onCategorySelect } : {})}
          />
        </div>

        {/* Anonymous embed visitor → "Login" in the same right slot; the
            slot button is a generic labelled glass button, reused as-is. */}
        {onLogin ? (
          <MyPacksButton onClick={onLogin} label="Login" />
        ) : (
          <MyPacksButton {...(onMyPacksClick ? { onClick: onMyPacksClick } : {})} />
        )}
      </div>
    </header>
  );
}
